import * as React from "react";
import axios from "axios";
import InventoryStatus from "@Tmp/InventoryStatus";
import { CartItemProps } from "@TmpApp/types/widgets/Cart";
import Price from "@Tmp/Price";
import { useAxios, useSettings, useUpdateEffect } from "@Tmp/Hooks";
import { Discount } from "@Tmp/Product";
import { Input } from "@Tmp/Fields";

const CartItem: React.FC<CartItemProps> = ({
    index,
    item,
    lastUpdate,
    shared,
    setData,
    getData,
    setLoading,
    ...props
}) => {
    const tmpSettings = useSettings();
    const [qty, setQty] = React.useState(item.qty);

    React.useEffect(() => {
        setQty(item.qty);
    }, [lastUpdate]);

    const { status: fetchStatus, error: fetchError } = useAxios(
        "put",
        "/wp-content/plugins/tmp/api/cart/updateItem.php",
        {
            id: item.id,
            qty: qty,
        },
        [qty],
        { autoRun: false }
    );

    useUpdateEffect(() => {
        if (fetchStatus === "loading") {
            setLoading(true);
            return;
        }
        if (fetchStatus === "success") {
            getData();
            tmp.cart.count();
        }
        if (fetchStatus === "error") {
            tmp.notice(fetchError?.response?.data?.message);
        }
        setLoading(false);
        props.triggerAnyChange();
        props.triggerItemChange();
    }, [fetchStatus]);

    const handleItemUpdate = (qtyVal: number) => {
        if (!qtyVal || qtyVal < 1) {
            return;
        }
        setQty(qtyVal);
    };

    const handleRemoveItem = () => {
        setData((prev) => {
            const newData = prev;
            newData.items.splice(index, 1);
            return newData;
        });

        setLoading(true);

        axios
            .delete("/wp-json/tmp/v1/cart/item/" + item.id, {
                headers: { "Content-type": "application/json" },
            })
            .then((response) => {
                if (response.data.code === "success") {
                    getData();
                    tmp.cart.count();
                } else {
                    setLoading(false);
                    tmp.notice("Could not remove item from cart.");
                }
            })
            .catch((error) => {
                setLoading(false);
                tmp.notice(error?.response?.data?.message);
            })
            .finally(() => {
                props.triggerAnyChange();
                props.triggerItemChange();
            });
    };

    return (
        <div className="tmp-cart__table-item elementor-row">
            <div className="elementor-column" style={{ width: "30%" }}>
                <div className="tmp-cart__table-item__image-container">
                    <img src={item.image} />
                </div>
            </div>
            <div className="elementor-column" style={{ width: "70%" }}>
                <div className="tmp-cart__table-item__info-container">
                    <div className="tmp-cart__table-item__name">
                        <b>{item.name}</b>
                        <div className="tmp-cart__table-item__sku">{item.sku}</div>
                    </div>
                    <div className="tmp-cart__table-item__price-content">
                        <Price
                            product={item}
                            appendPriceText={" / " + (props.settings?.cart_item_qty_text || "Stk.")}
                            settings={props.settings}
                        />
                    </div>
                    {qty > 1 && (
                        <div className="tmp-cart__table-item__price-content">
                            <Price
                                hideDiscount
                                prependPrice={
                                    qty +
                                    " " +
                                    (props.settings?.cart_item_qty_text || "Stk.") +
                                    " / "
                                }
                                price={item.price_total + " " + tmpSettings.currency}
                                settings={props.settings}
                            />
                        </div>
                    )}

                    <div className="tmp-cart__table-item__amount">
                        <Input
                            disabled={shared}
                            value={qty}
                            onChange={(e) => {
                                handleItemUpdate(parseInt(e.target.value));
                            }}
                            input={{
                                className: "tmp-add-to-cart__amount-input",
                                type: "number",
                                step: "1",
                                min: "1",
                                max: "999",
                            }}
                            iconBefore={!shared ? "fa fa-minus" : undefined}
                            iconBeforeContainer={
                                !shared
                                    ? {
                                          className: "cursor-pointer",
                                          onClick: () => {
                                              if (!shared) {
                                                  handleItemUpdate(qty - 1);
                                              }
                                          },
                                      }
                                    : undefined
                            }
                            iconAfter={!shared ? "fa fa-plus" : undefined}
                            iconAfterContainer={
                                !shared
                                    ? {
                                          className: "cursor-pointer",
                                          onClick: () => {
                                              if (!shared) {
                                                  handleItemUpdate(qty + 1);
                                              }
                                          },
                                      }
                                    : undefined
                            }
                        />
                    </div>
                    <div className="tmp-cart__table-item__remove">
                        <div className="tmp-cart__table-item__inventory">
                            {(props.settings?.show_inventory_status === undefined
                                ? true
                                : props.settings?.show_inventory_status === "yes") && (
                                <InventoryStatus product={item} />
                            )}
                        </div>

                        {!shared && (
                            <div
                                className="tmp-cart__remove"
                                onClick={() => {
                                    handleRemoveItem();
                                }}
                            >
                                <i className="fas fa-times fa-lg" />
                            </div>
                        )}
                    </div>
                </div>
            </div>
            <Discount product={item} percentage={tmpSettings.logged_in} style={{ top: "1rem" }} />
        </div>
    );
};

export default CartItem;
